import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { distinctUntilChanged, filter, switchMap } from 'rxjs'
import type { DocumentsTabViewModelFactory } from '@/features/documents'
import { Member } from '@/features/iam/members/Member'
import type { FetchMemberFn, GetCurrentMemberFn } from '@/features/iam'
import type {
  AdvisorClientsTabViewModelFactory,
  ClientAssignmentViewModelFactory,
} from '@/features/client-user-management'
import type {
  PromptCreateJobFn,
  UserJobsTabViewModelFactory,
} from '@/features/jobs'
import { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { activation$, type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type AdvisorClientsTabViewModel } from './AdvisorClientsTabViewModel'
import { type ClientAssignmentViewModel } from '@/features/client-user-management/ClientAssignmentViewModel'

/**
 * Tabs displayed in the client user details view.
 */
export const clientUserDetailsTabs = ['Jobs', 'Documents', 'Clients'] as const

export type ClientUserDetailsTab = (typeof clientUserDetailsTabs)[number]

type JobsTabViewModel = ReturnType<UserJobsTabViewModelFactory>

type DocumentsTabViewModel = ReturnType<DocumentsTabViewModelFactory>

export class ClientUserDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The client being viewed.
   */
  @observable
  accessor member: Member = Member.none

  /**
   * The currently selected tab.
   */
  @observable
  accessor tab: ClientUserDetailsTab = 'Jobs'

  /**
   * Jobs tab view model. Jobs load once the tab is first shown.
   */
  readonly jobsTabVm: JobsTabViewModel

  /**
   * Documents tab view model. Documents load once the tab is first shown.
   */
  readonly documentsTabVm: DocumentsTabViewModel

  /**
   * Clients tab view model, shown when the client is an advisor.
   */
  readonly clientsTabVm: AdvisorClientsTabViewModel

  /**
   * The client's advisors, or whether the client is an advisor.
   */
  readonly clientAssignmentVm: ClientAssignmentViewModel

  constructor(
    readonly userPublicId: Id,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _canAccessClientUserManagement: () => boolean,
    private readonly _promptCreateJob: PromptCreateJobFn,
    createUserJobsTabViewModel: UserJobsTabViewModelFactory,
    createDocumentsTabViewModel: DocumentsTabViewModelFactory,
    createAdvisorClientsTabViewModel: AdvisorClientsTabViewModelFactory,
    createClientAssignmentViewModel: ClientAssignmentViewModelFactory,
  ) {
    this.jobsTabVm = createUserJobsTabViewModel({
      userPublicId: () => this.member.user.publicId,
      workspaces: () => [this.currentUserWorkspace],
    })
    this.documentsTabVm = createDocumentsTabViewModel({
      userPublicId: () => this.member.user.publicId,
      userMemberships: () => [this.member.toMembership()],
      currentUserMemberships: () => [this._getCurrentMember().toMembership()],
    })
    this.clientsTabVm = createAdvisorClientsTabViewModel({
      getAdvisorUserPublicId: () => this.member.user.publicId,
    })
    this.clientAssignmentVm = createClientAssignmentViewModel({
      getUserPublicId: () => this.userPublicId,
      canHaveAssignment: () => true,
    })
    this._lifecycle.manage(this.clientAssignmentVm)

    // Tabs activate once the client has loaded, because they read the
    // client's public ID.
    this._lifecycle.manage(
      toStream(() => (this.hasMember ? this.currentTab : null), true).pipe(
        filter((tab) => tab !== null),
        distinctUntilChanged(),
        switchMap((tab) => activation$(this._tabVm(tab))),
      ),
    )
  }

  activate() {
    this._lifecycle.activate()
  }

  @action.bound
  deactivate() {
    this._lifecycle.deactivate()
    this.tab = 'Jobs'
  }

  /**
   * Whether the client was found. The page keeps loading while it is not.
   */
  @computed
  get hasMember() {
    return this.member !== Member.none
  }

  /**
   * The workspace available to the current logged in user (The admin)
   */
  @computed
  get currentUserWorkspace() {
    return this._getCurrentMember().workspace
  }

  /**
   * Whether the user should be allowed access to view user details.
   */
  @computed
  get canAccessUserDetails() {
    return this._canAccessClientUserManagement()
  }

  /**
   * The selected tab, or the first one when the selected tab is hidden.
   */
  @computed
  get currentTab(): ClientUserDetailsTab {
    return this.tabs.includes(this.tab) ? this.tab : 'Jobs'
  }

  /**
   * The tabs shown for this client.
   */
  @computed
  get tabs(): ClientUserDetailsTab[] {
    return clientUserDetailsTabs.filter(
      (tab) =>
        tab !== 'Clients' || this.clientAssignmentVm.shouldShowClientsTab,
    )
  }

  @computed
  get canCreateJobForUser() {
    return this._getCurrentMember().permissions.adminJobCreate
  }

  /**
   * Set the currently selected tab.
   */
  @action.bound
  setTab(tab: ClientUserDetailsTab) {
    this.tab = tab
  }

  /**
   * Loads the client.
   */
  fetch = task(async (signal: AbortSignal) => {
    const member = await this._fetchMember(
      {
        workspaceId: Id.assert(this.currentUserWorkspace.id),
        userPublicId: Id.assert(this.userPublicId),
      },
      signal,
    )
    signal.throwIfAborted()

    if (member) {
      this._setMember(member)
    }
  })

  /**
   * Creates job for user.
   */
  @action.bound
  createJob() {
    if (!this.member.user.publicId) {
      return
    }

    this._promptCreateJob({
      answers: [],
      ownerScopedInput: {
        publicId: this.member.user.publicId,
        workspaceIds: [Id.assert(this.currentUserWorkspace.id)],
      },
    }).catch(() => undefined)
  }

  private _tabVm(
    tab: ClientUserDetailsTab,
  ): JobsTabViewModel | DocumentsTabViewModel | AdvisorClientsTabViewModel {
    switch (tab) {
      case 'Jobs':
        return this.jobsTabVm
      case 'Documents':
        return this.documentsTabVm
      case 'Clients':
        return this.clientsTabVm
    }
  }

  @action.bound
  private _setMember(member: Member) {
    this.member = member
  }
}
